import { useTranslation } from 'react-i18next';

import useAiChatToolLabel from '../../hooks/useAiChatToolLabel';
import useAiChatToolStatus from '../../hooks/useAiChatToolStatus';
import useOpenImpactAnalysis from '../../hooks/useOpenImpactAnalysis';
import AiChatToolCard from '../AiChatToolCard';

import type { ReactElement, ReactNode } from 'react';
import type { IImpactScenarioState, IImpactUnitsSkipped } from '../../interfaces/IAiChatTools';
import type IImpactAnalysisToolCall from '../../interfaces/IImpactAnalysisToolCall';

import styles from '../../sass/AiChat.module.scss';

type TPart = IImpactAnalysisToolCall['part'];

/**
 * Name of the scenario `skipped[index]` belongs to.
 *
 * `skipped` runs parallel to the scenarios the input gave: on a create, the scenarios in order;
 * on an update, `updateScenarios` then `addScenarios`. The output lists every scenario of the
 * analysis in creation order, so a created one is read there by position — from the tail on an
 * update, where the added scenarios are the newest — and an updated one by its id.
 */
const readSkippedScenario = (part: TPart, index: number): IImpactScenarioState | undefined => {
  const scenarios = part.state === 'output-available' && part.output.ok ? part.output.scenarios : [];

  if (part.type === 'tool-createImpactAnalysis') {
    return scenarios[index];
  }

  const updated = part.input?.updateScenarios ?? [];
  const added = part.input?.addScenarios ?? [];

  if (index < updated.length) {
    return scenarios.find(({ scenarioId }) => scenarioId === updated[index].scenarioId);
  }

  return scenarios[scenarios.length - added.length + (index - updated.length)];
};

/** What the call was made with, under the header: the name, or the id of the analysis. */
const readInputName = (part: TPart, fallback: (id: number) => string): string | undefined => {
  if (part.input?.name) {
    return part.input.name;
  }

  return part.type === 'tool-updateImpactAnalysis' && part.input?.analysisId
    ? fallback(part.input.analysisId)
    : undefined;
};

/**
 * The `createImpactAnalysis` and `updateImpactAnalysis` tool calls: an impact analysis of a map
 * made or changed, scenarios included. One component: the two answer with the same shape.
 *
 * A step row: the header names the analysis; under it, open, with no toggle, one line with how
 * many scenarios it holds and how many distinct units they hit, and whatever a scenario's units
 * request could not take. Unit ids are tool-side ids and are never listed — a skipped entry
 * says how many ids were not on the map. A refusal is an answer for the model, not a failure:
 * the row shows the reason on one line.
 *
 * The analysis is a normal one of the map — the chat does not own it — so the details offer to
 * open it in the map's Impact Scenarios panel.
 */
function ImpactAnalysisToolCall({ part }: IImpactAnalysisToolCall): ReactElement {
  const { t } = useTranslation();
  const isCreate = part.type === 'tool-createImpactAnalysis';
  const label = useAiChatToolLabel(isCreate ? 'createImpactAnalysis' : 'updateImpactAnalysis');
  const status = useAiChatToolStatus(part.state);
  const openImpactAnalysis = useOpenImpactAnalysis();

  const output = part.state === 'output-available' ? part.output : undefined;
  const fallbackName = (id: number) => t('aiChat.tools.impactAnalysisFallbackName', { id });

  // Under the header: which analysis while the call runs, or the reason of a refusal. Once the
  // call is done the header names the analysis itself, so the line would only say it again.
  const readNote = (): string | undefined => {
    if (output) {
      return output.ok ? undefined : output.reason;
    }

    return readInputName(part, fallbackName);
  };

  const readSummary = (): string | undefined => {
    if (!output) {
      return status;
    }

    if (!output.ok) {
      return t('aiChat.tools.impactAnalysisRefused');
    }

    return t(isCreate ? 'aiChat.tools.impactAnalysisCreated' : 'aiChat.tools.impactAnalysisUpdated', {
      name: output.name,
    });
  };

  // What the analysis holds now: its scenarios, and the units they hit between them, each unit
  // once however many scenarios name it.
  const readCounts = (scenarios: IImpactScenarioState[]): string => t('aiChat.tools.impactRunCounts', {
    scenarios: t('aiChat.tools.impactScenarioCount', { count: scenarios.length }),
    units: t('aiChat.tools.impactUnitCount', { count: new Set(scenarios.flatMap(({ unitIds }) => unitIds)).size }),
  });

  const renderSkipped = (skipped: IImpactUnitsSkipped, index: number): ReactNode => {
    if (!skipped.unknownUnitIds.length && !skipped.datasourceProblem) {
      return null;
    }

    const name = readSkippedScenario(part, index)?.name
      ?? t('aiChat.tools.impactScenarioFallbackName', { index: index + 1 });

    return (
      <li key={`${index}-${name}`}>
        {skipped.unknownUnitIds.length ? (
          <p>{t('aiChat.tools.impactUnknownUnits', { name, count: skipped.unknownUnitIds.length })}</p>
        ) : null}
        {skipped.datasourceProblem ? (
          <p>{t('aiChat.tools.impactDatasourceProblem', { name, problem: skipped.datasourceProblem })}</p>
        ) : null}
      </li>
    );
  };

  const handleOpen = () => {
    if (output?.ok) {
      openImpactAnalysis({
        valueChainMapId: output.valueChainMapId, analysisId: output.analysisId, name: output.name,
      });
    }
  };

  const hasSkipped = output?.ok && output.skipped.some(
    ({ unknownUnitIds, datasourceProblem }) => unknownUnitIds.length || datasourceProblem,
  );

  const details = output?.ok ? (
    <>
      <p>{readCounts(output.scenarios)}</p>
      {hasSkipped ? <ul className={styles.toolWarnings}>{output.skipped.map(renderSkipped)}</ul> : null}
      <button type="button" className={styles.toolToggle} onClick={handleOpen}>
        {t('aiChat.tools.impactOpenPanel')}
      </button>
    </>
  ) : undefined;

  return (
    <AiChatToolCard
      name={label}
      status={readSummary()}
      details={details}
      isDetailsAlwaysOpen
      errorText={part.errorText}
    >
      {readNote()}
    </AiChatToolCard>
  );
}

export default ImpactAnalysisToolCall;
